import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Youtube, Loader2, Info } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { fmtMoney, fmtNum } from "@/utils/currency";

const ytIdFromUrl = (url) => {
  const m = /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/.exec(url || "");
  return m ? m[1] : null;
};

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
const fmtSecs = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`;
};

/**
 * Per-video YouTube analytics, synced read-only from the official Data + Analytics APIs.
 * RPM — YouTube's Shorts definition: estimated revenue / ENGAGED views * 1000.
 * Total/public views (plays) are never the Shorts RPM denominator.
 */
export const VideoAnalyticsPanel = ({ youtubeUrl }) => {
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const ytId = ytIdFromUrl(youtubeUrl);

  useEffect(() => {
    setData(null);
    setMissing(false);
    if (!ytId) return;
    axios.get(`${API}/youtube/videos/${ytId}`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch(() => setMissing(true));
  }, [ytId]);

  const retention = useMemo(
    () => (data?.retention || []).map((r) => ({
      pct: Math.round((r.ratio || 0) * 100),
      watch: r.watch_ratio != null ? +(r.watch_ratio * 100).toFixed(1) : null,
      relative: r.relative != null ? +(r.relative * 100).toFixed(1) : null,
    })),
    [data]
  );

  if (!ytId) return null;

  if (missing) {
    return (
      <div data-testid="video-yt-analytics" className="rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] p-6 text-center">
        <Youtube className="mx-auto h-6 w-6 text-[var(--pv-text-4)]" />
        <p className="mt-2 text-xs text-[var(--pv-text-4)]">No synced YouTube analytics for this video yet — connect the channel and press Sync now.</p>
      </div>
    );
  }
  if (!data) {
    return <p className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>;
  }

  const s = data.stats || {};
  const metrics = [
    { key: "views", label: "Views", value: fmt(data.views) },
    { key: "engaged", label: "Engaged views", value: fmt(s.engagedViews) },
    { key: "likes", label: "Likes", value: fmt(s.likes) },
    { key: "comments", label: "Comments", value: fmt(s.comments) },
    { key: "shares", label: "Shares", value: fmt(s.shares) },
    { key: "watch", label: "Watch time (hrs)", value: s.estimatedMinutesWatched != null ? (s.estimatedMinutesWatched / 60).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—" },
    { key: "avd", label: "Avg view duration", value: fmtSecs(s.averageViewDuration) },
    { key: "avp", label: "Avg view %", value: s.averageViewPercentage != null ? `${Number(s.averageViewPercentage).toFixed(1)}%` : "—" },
    { key: "duration", label: "Video duration", value: fmtSecs(data.duration_seconds) },
    { key: "subsg", label: "Subs gained", value: fmt(s.subscribersGained) },
    { key: "subsl", label: "Subs lost", value: fmt(s.subscribersLost) },
    { key: "revenue", label: "Est. revenue", value: data.estimated_revenue != null ? fmtMoney(Number(data.estimated_revenue)) : "—" },
    { key: "rpm", label: "RPM", value: data.rpm != null ? fmtMoney(Number(data.rpm)) : "—" },
  ];

  return (
    <div data-testid="video-yt-analytics" className="rounded-3xl border border-rose-500/20 bg-[var(--pv-card)] p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">
          <Youtube className="h-4 w-4 text-rose-400" /> YouTube analytics
        </h3>
        <p className="text-[10px] text-[var(--pv-text-4)]">
          {data.published_at && <>published {new Date(data.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · </>}
          synced {new Date(data.synced_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
        {metrics.map((m) => (
          <div key={m.key} data-testid={`yt-metric-${m.key}`} className="rounded-2xl bg-[var(--pv-subtle)] px-3.5 py-3">
            <p className="font-display text-base font-bold text-[var(--pv-text)]">{m.value}</p>
            <p className="text-[9px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{m.label}</p>
          </div>
        ))}
      </div>

      <p className="mt-3 flex items-start gap-1.5 text-[10px] text-[var(--pv-text-4)]">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        RPM = estimated revenue ÷ engaged views × 1,000 — YouTube's Shorts RPM definition. Engaged views (not total plays) is the denominator; both metrics are shown separately above.
        {data.estimated_revenue == null && " Revenue metrics are unavailable for this channel/video (not monetized or monetary scope not granted)."}
        {data.estimated_revenue != null && data.rpm == null && " RPM is unavailable because engaged views were not returned for this video."}
      </p>

      <p data-testid="yt-shorts-note" className="mt-1.5 flex items-start gap-1.5 text-[10px] text-[var(--pv-text-4)]">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        Shorts "Stayed to watch" / "Swiped away": not exposed by the official YouTube API — shown as unavailable instead of approximated. Engaged views is the closest official metric and is synced above.
      </p>

      {retention.length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Audience retention</p>
          <div data-testid="yt-retention-chart" className="mt-2 h-56 w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] p-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={retention} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="var(--pv-border)" strokeDasharray="3 3" />
                <XAxis dataKey="pct" tick={{ fontSize: 10, fill: "var(--pv-text-4)" }} tickFormatter={(v) => `${v}%`} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "var(--pv-text-4)" }} tickFormatter={(v) => `${v}%`} domain={[0, "auto"]} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "var(--pv-text-4)" }} hide={retention.every((r) => r.relative == null)} />
                <Tooltip
                  contentStyle={{ background: "var(--pv-card)", border: "1px solid var(--pv-border)", borderRadius: 12, fontSize: 11 }}
                  labelFormatter={(v) => `${v}% through the video`}
                  formatter={(v, name) => [`${v}%`, name === "watch" ? "Audience retained" : "Relative performance"]}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} formatter={(v) => (v === "watch" ? "Audience retained" : "Relative retention")} />
                <Line yAxisId="left" type="monotone" dataKey="watch" stroke="#06b6d4" strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="relative" stroke="#a78bfa" strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};
